// features/projects/templates/professional/widgets/solution-chains.js
// ═══════════════════════════════════════════════════════════
// SOLUTION CHAINS — Tier-A widget
//
// Q: "Where can findings I haven't yet verified borrow
//     defensibility from findings that already survived
//     adversarial review?"
// Cross-finding causal reasoning via field-shape similarity.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import { findSolutionChains } from '../../../../../lib/continuum/research/solution-chains';
import { WidgetShell, LayerSection, CaveatStrip } from './findings-status';

const VERDICT_COLOR = {
  PARALLEL:        L.sage,
  'NEAR-PARALLEL': L.gold,
  DIVERGENT:       L.terra,
};
const PHASE_A_COLOR = {
  survived:               L.sage,
  'survived-respecified': L.gold,
};

export default function SolutionChainsWidget() {
  const { loading, comparisons } = useWidgetData();
  const chains = useMemo(
    () => findSolutionChains({ comparisons: comparisons || [], dynamics: DYNAMICS, perTargetLimit: 1 }),
    [comparisons],
  );

  if (loading) {
    return <WidgetShell label="Solution Chains"><div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div></WidgetShell>;
  }

  if (chains.length === 0) {
    // Empty state — short paragraph, no caveat-strip clutter.
    // The caveat layer belongs where chains exist and need
    // qualification; not when there's nothing to qualify.
    return (
      <WidgetShell label="Solution Chains">
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No chains surfaced yet. Chains link Phase-A-survived findings to structurally-similar unverified ones — both sides need data first.
        </div>
      </WidgetShell>
    );
  }

  return (
    <WidgetShell label={`Solution Chains (${chains.length})`}>
      <div style={{ fontSize: 12, color: L.mid, fontFamily: FONT.sans, lineHeight: 1.55 }}>
        Each chain proposes that an unverified finding could borrow the defensibility (and the prescription shape) of a Phase-A-survived finding it's structurally similar to. Use as a research prior, not a confirmed claim.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        {chains.slice(0, 4).map(ch => <ChainRow key={`${ch.source.id}-${ch.target.id}`} chain={ch} />)}
      </div>
      <LayerSection label="Takeaway" accent={L.gold}>
        Chains are the methodology's compounding mechanism. Each verified source pulls multiple unverified targets up the defensibility ladder.
      </LayerSection>
      <CaveatStrip text="Field-shape similarity (cluster, gap profile, concept overlap) — does not read raw mechanism content. A high score is a hypothesis worth testing, not evidence of mechanism transfer." />
    </WidgetShell>
  );
}

function ChainRow({ chain }) {
  const { source, sourceDyn, target, targetDyn, score, factors, reasoning } = chain;
  const sourceColor = source.phase_a_status === 'survived-respecified' ? L.gold : L.sage;
  const targetVerdict = (target.verdict || '').toUpperCase();
  const targetColor = targetVerdict === 'DIVERGENT' ? L.terra : L.dim;

  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${L.b1}`,
      borderRadius: 5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 8, fontFamily: FONT.sans, fontSize: 12,
      }}>
        <span style={{ color: sourceColor, fontWeight: 600 }}>{sourceDyn.label}</span>
        <Pill color={sourceColor} text={source.phase_a_status === 'survived-respecified' ? 'SURVIVED · RESPECIFIED' : 'SURVIVED'} />
        <span style={{ color: L.dim, fontFamily: FONT.mono, fontSize: 14 }}>→</span>
        <span style={{ color: targetColor, fontWeight: 600 }}>{targetDyn.label}</span>
        {target.verdict && <Pill color={targetColor} text={targetVerdict} />}
        <span style={{ marginLeft: 'auto', fontFamily: FONT.mono, fontSize: 9, color: L.dim }}>
          score {score}
        </span>
      </div>
      <ul style={{
        margin: '0 0 8px 0', paddingLeft: 16,
        fontFamily: FONT.sans, fontSize: 11, color: L.mid, lineHeight: 1.55,
      }}>
        {factors.map((f, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {f.label} <span style={{ color: L.dim, fontFamily: FONT.mono, fontSize: 9 }}>+{f.weight}</span>
          </li>
        ))}
      </ul>
      <div style={{
        fontFamily: FONT.sans, fontSize: 11, color: L.mid, lineHeight: 1.55,
        fontStyle: 'italic',
      }}>
        {reasoning.adaptation}
      </div>
    </div>
  );
}

function Pill({ color, text }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 3,
      border: `1px solid ${color}60`, background: `${color}15`, color,
      letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase',
      fontSize: 9, fontFamily: FONT.mono,
    }}>{text}</span>
  );
}
